import { BaseApiClient } from './base'

export class UserBooksApi extends BaseApiClient {
  async getUserBooks(params?: { page?: number; page_size?: number }): Promise<{ books: any[]; pagination?: any }> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    
    const queryString = queryParams.toString()
    const url = queryString ? `/user/books?${queryString}` : '/user/books'
    
    return this.request(url)
  }

  async getUserBook(bookId: string): Promise<{ book: any }> {
    return this.request(`/user/books/${bookId}`)
  }

  async deleteUserBook(bookId: string): Promise<{ success: boolean }> {
    return this.request(`/user/books/${bookId}`, {
      method: 'DELETE',
    })
  }

  async createUserBook(data: {
    title_original: string
    title_international: string
    author_original: string
    author_international: string
    description?: string
    original_language?: string
    estimated_pages?: number
    original_file: File
    cover_image?: File
  }): Promise<{ success: boolean; book_id: string }> {
    const form = new FormData()
    form.append('title_original', data.title_original)
    form.append('title_international', data.title_international)
    form.append('author_original', data.author_original)
    form.append('author_international', data.author_international)
    if (data.description) {
      form.append('description', data.description)
    }
    if (data.original_language) {
      form.append('original_language', data.original_language)
    }
    if (data.estimated_pages) {
      form.append('estimated_pages', data.estimated_pages.toString())
    }
    form.append('original_file', data.original_file)
    if (data.cover_image) {
      form.append('cover_image', data.cover_image)
    }

    return this.requestFormData('/user/books', form, { method: 'POST' })
  }

  async updateUserBook(bookId: string, data: {
    title_original?: string
    title_international?: string
    author_original?: string
    author_international?: string
    original_language?: string
    description?: string
    cover_image?: File
  }): Promise<{ success: boolean }> {
    const form = new FormData()
    if (data.title_original) form.append('title_original', data.title_original)
    if (data.title_international) form.append('title_international', data.title_international)
    if (data.author_original) form.append('author_original', data.author_original)
    if (data.author_international) form.append('author_international', data.author_international)
    if (data.original_language) form.append('original_language', data.original_language)
    if (data.description) form.append('description', data.description)
    if (data.cover_image) form.append('cover_image', data.cover_image)

    return this.requestFormData(`/user/books/${bookId}`, form, { method: 'PUT' })
  }

  async analyzeUserBookFile(file: File): Promise<{ success: boolean; metadata: any }> {
    const form = new FormData()
    form.append('file', file)

    return this.requestFormData('/user/books/analyze-file', form, { method: 'POST' })
  }

  async addUserBookTranslation(data: {
    book_id: string
    language: string
    cefr_level: string | null
    file: File
  }): Promise<{ success: boolean, translation_id: string }> {
    const formData = new FormData()
    formData.append('language', data.language)
    if (data.cefr_level) formData.append('cefr_level', data.cefr_level)
    formData.append('file', data.file)

    return this.requestFormData(`/user/books/${data.book_id}/translations`, formData, { method: 'POST' })
  }

  async queueUserBookAutoTranslation(
    bookId: string,
    language: string,
    cefrLevel: string,
    estimateId?: string,
    provider: string = 'gemini'
  ): Promise<{ success: boolean, job_id: string, translation_id: string, provider: string }> {
    const body: any = { 
      language, 
      cefr_level: cefrLevel, 
      provider 
    }
    
    if (estimateId) {
      body.estimate_id = estimateId
    }
    
    return this.request(`/user/books/${bookId}/queue-translation`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async deleteUserBookTranslation(bookId: string, translationId: string): Promise<{ success: boolean; refunded?: boolean; refund_amount?: number }> {
    return this.request(`/user/books/${bookId}/translations/${translationId}`, {
      method: 'DELETE',
    })
  }

  async downloadUserBook(bookId: string, translationId: string): Promise<void> {
    return this.downloadFile(`/user/books/${bookId}/download/${translationId}`, 'book')
  }

  async downloadUserBookOriginal(bookId: string): Promise<void> {
    return this.downloadFile(`/user/books/${bookId}/download-original`, 'book_original')
  }

  async getActiveTranslations(): Promise<{
    success: boolean
    translations: Array<{
      job_id: number
      translation_id: string
      book_id: string
      book_title: string | null
      target_language: string
      cefr_level: string
      status: 'queued' | 'running'
      current_batch: number
      total_batches: number
      progress_percent: number
      created_at: string
      updated_at: string
    }>
  }> {
    return this.request('/user/active-translations')
  }

  async getJobStatus(jobId: number): Promise<{
    success: boolean
    job: {
      id: number
      status: 'queued' | 'running' | 'succeeded' | 'failed'
      attempts: number
      max_attempts: number
      created_at: string
      updated_at: string
      last_error: string | null
    }
  }> {
    return this.request(`/job-status/${jobId}`)
  }
}

